import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Card, Form, Input, Button, message, Skeleton } from 'antd';
import api from '../services/api';
import { useMerchantDetail } from '../hooks/useMerchantDetail';
import LocationPicker from '../components/LocationPicker';
import type { LocationPayload } from '../types/location';

export default function MerchantEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);
  const [location, setLocation] = React.useState<LocationPayload | null>(null);
  const { data: merchant, loading: merchantLoading } = useMerchantDetail(id);

  useEffect(() => {
    if (merchant) {
      form.setFieldsValue({
        name: merchant.name || '',
        email: merchant.email || '',
        phone: merchant.phone || '',
        address: merchant.address || '',
        restaurantName: merchant.restaurantName || '',
        restaurantAddress: merchant.restaurantAddress || '',
        latitude: merchant.latitude ?? undefined,
        longitude: merchant.longitude ?? undefined,
      });
      const addr = merchant.restaurantAddress || '';
      const lat = merchant.latitude;
      const lng = merchant.longitude;
      const hasCoords =
        lat != null &&
        lng != null &&
        typeof lat === 'number' &&
        typeof lng === 'number';
      if (hasCoords) {
        setLocation({ address: addr, lat, lng });
      } else {
        setLocation(null);
      }
    }
  }, [merchant, form]);

  useEffect(() => {
    if (!merchant) return;
    const addr = (merchant.restaurantAddress || '').trim();
    const hasCoords =
      merchant.latitude != null &&
      merchant.longitude != null &&
      Number.isFinite(Number(merchant.latitude)) &&
      Number.isFinite(Number(merchant.longitude));
    if (!addr || addr.length < 3 || hasCoords) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/geocode/search', { params: { q: addr } });
        const results = (
          res.data as {
            results?: Array<{
              formattedAddress: string;
              lat: number;
              lng: number;
            }>;
          }
        ).results;
        const first = results?.[0];
        if (!cancelled && first) {
          setLocation({
            address: first.formattedAddress || addr,
            lat: first.lat,
            lng: first.lng,
          });
          form.setFieldsValue({
            restaurantAddress: first.formattedAddress || addr,
            latitude: first.lat,
            longitude: first.lng,
          });
        }
      } catch {
        /* geocode optional */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [merchant, form]);

  const onFinish = async (values: Record<string, unknown>) => {
    if (!id) return;
    try {
      setLoading(true);
      const restaurantAddress =
        location?.address ?? String(values.restaurantAddress || '').trim();
      const lat = location?.lat ?? values.latitude;
      const lng = location?.lng ?? values.longitude;
      if (lat == null || lng == null) {
        message.error('Vui lòng tìm kiếm địa chỉ hoặc chọn vị trí trên bản đồ');
        return;
      }
      const payload: Record<string, unknown> = {
        name: String(values.name || '').trim(),
        email: String(values.email || '')
          .trim()
          .toLowerCase(),
        phone: values.phone ? String(values.phone).trim() : undefined,
        address: values.address ? String(values.address).trim() : undefined,
        restaurantName: String(values.restaurantName || '').trim(),
        restaurantAddress: restaurantAddress || undefined,
        latitude: typeof lat === 'number' ? lat : Number(lat),
        longitude: typeof lng === 'number' ? lng : Number(lng),
        isActive: merchant?.is_active ?? true,
      };
      await api.put(`/merchants/${id}`, payload);
      message.success('Cập nhật thành công');
      navigate(`/merchants/${id}`);
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } } };
      message.error(ax.response?.data?.message || 'Cập nhật thất bại');
    } finally {
      setLoading(false);
    }
  };

  const displayName = merchant?.restaurantName || merchant?.name || 'Merchant';

  if (merchantLoading && !merchant) {
    return (
      <div className="merchant-detail-page">
        <Skeleton active paragraph={{ rows: 8 }} />
      </div>
    );
  }

  if (!merchant) {
    return (
      <div className="merchant-detail-page">
        <p>Merchant not found</p>
        <Link to="/merchants">Back to list</Link>
      </div>
    );
  }

  return (
    <div className="merchant-detail-page">
      <header className="merchant-detail-header">
        <h1 className="merchant-detail-title">Edit Merchant</h1>
        <nav className="merchant-detail-breadcrumb">
          <Link to="/">Dashboard</Link>
          <span className="merchant-detail-breadcrumb-sep">&gt;</span>
          <Link to="/merchants">Merchants</Link>
          <span className="merchant-detail-breadcrumb-sep">&gt;</span>
          <Link to={`/merchants/${id}`}>{displayName}</Link>
          <span className="merchant-detail-breadcrumb-sep">&gt;</span>
          <span className="merchant-detail-breadcrumb-current">Edit</span>
        </nav>
      </header>

      <Card style={{ maxWidth: 720 }}>
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          style={{ maxWidth: 560 }}
        >
          <Form.Item
            name="name"
            label="Owner Name"
            rules={[{ required: true }]}
          >
            <Input placeholder="Tên chủ quán" />
          </Form.Item>
          <Form.Item
            name="email"
            label="Email"
            rules={[{ required: true }, { type: 'email' }]}
          >
            <Input type="email" placeholder="email@example.com" />
          </Form.Item>
          <Form.Item name="phone" label="Phone">
            <Input placeholder="Số điện thoại" />
          </Form.Item>
          <Form.Item name="address" label="Owner Address">
            <Input placeholder="Địa chỉ chủ quán" />
          </Form.Item>
          <Form.Item
            name="restaurantName"
            label="Restaurant Name"
            rules={[{ required: true }]}
          >
            <Input placeholder="Tên quán" />
          </Form.Item>
          <Form.Item
            label="Restaurant Address"
            required
            help={
              location?.lat != null && location?.lng != null
                ? `Đã chọn vị trí (Lat: ${location.lat.toFixed(
                    4
                  )}, Lng: ${location.lng.toFixed(
                    4
                  )}) – lat/lng là nguồn chân lý`
                : 'Tìm kiếm địa chỉ hoặc kéo ghim trên bản đồ để chọn vị trí'
            }
          >
            <LocationPicker
              key={`loc-${merchant.id}-${merchant.latitude ?? 'x'}-${
                merchant.longitude ?? 'y'
              }`}
              deferMapMs={250}
              initialAddress={merchant?.restaurantAddress || ''}
              initialLat={
                merchant?.latitude != null
                  ? Number(merchant.latitude)
                  : undefined
              }
              initialLng={
                merchant?.longitude != null
                  ? Number(merchant.longitude)
                  : undefined
              }
              onChange={payload => {
                setLocation(payload);
                form.setFieldsValue({
                  restaurantAddress: payload.address,
                  latitude: payload.lat,
                  longitude: payload.lng,
                });
              }}
              onAddressChange={addr => {
                form.setFieldsValue({ restaurantAddress: addr });
                if (location)
                  setLocation(p => (p ? { ...p, address: addr } : null));
              }}
            />
          </Form.Item>
          <Form.Item name="restaurantAddress" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="latitude" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="longitude" hidden>
            <Input />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading}>
              Save Changes
            </Button>
            <Button
              style={{ marginLeft: 8 }}
              onClick={() => navigate(`/merchants/${id}`)}
            >
              Cancel
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
}
